import { keys, uniq, values } from 'lodash';
import React, { useMemo } from 'react';
import { Line } from 'react-chartjs-2';

import { CircularProgress } from '@mui/material';

import { useVulnerabilitiesAggregation } from 'queries/search/use-vulnerabilities-aggregation';

interface Props {
    termName: string;
    termValue: string;
}

const CVELineChart: React.FC<Props> = ({ termName, termValue }) => {
    const { data, isLoading } = useVulnerabilitiesAggregation(
        { termName, termValue },
        { select: ({ bulletingAggregation }) => bulletingAggregation }
    );

    const labels = useMemo(() => {
        return uniq(
            values(data)
                .map((entry) => keys(entry))
                .flat()
        );
    }, [data]);

    const getGradient = (ctx, chartArea, bgColor) => {
        const gradient = ctx.createLinearGradient(chartArea.left, 0, chartArea.right, 0);
        gradient.addColorStop(0, bgColor[0]);
        gradient.addColorStop(1, bgColor[1]);
        return gradient;
    };

    const getBackground = (bgColor) => (context) => {
        const { ctx, chartArea } = context.chart;
        return !chartArea ? null : getGradient(ctx, chartArea, bgColor);
    };

    const getDataSet = (slice, label, bgColor) => {
        if (slice) {
            return {
                data: Object.values(slice),
                label: label,
                responsive: true,
                backgroundColor: getBackground(bgColor),
                gradientColor: bgColor, // Field need for generateLabels method
                borderColor: 'transparent',
                pointRadius: 0
            };
        }
    };

    if (isLoading) {
        return <CircularProgress />;
    }

    return (
        <Line
            height={203}
            data={{
                labels: labels,
                datasets: [
                    getDataSet(data?.high, 'high', ['#FFECD2', '#FCB69F']),
                    getDataSet(data?.medium, 'medium', ['rgba(255, 236, 210, 0.6)', 'rgba(252, 182, 159, 0.6)']),
                    getDataSet(data?.low, 'low', ['rgba(255, 236, 210, 0.25)', 'rgba(252, 182, 159, 0.25)']),
                    getDataSet(data?.zero, 'zero', ['#FAFAFA', '#FAFAFA'])
                ].filter((set) => !!set)
            }}
            options={{
                legend: {
                    position: 'bottom',
                    labels: {
                        generateLabels: (chart) => {
                            const data = chart.data;
                            if (data.datasets.length) {
                                return data.datasets.map((set, index) => ({
                                    text: set.label,
                                    datasetIndex: index,
                                    borderRadius: 3,
                                    lineWidth: set.label === 'zero' ? 1 : 0,
                                    fillStyle: set.gradientColor[1]
                                }));
                            }
                        }
                    }
                },
                scales: {
                    yAxes: [
                        {
                            display: false,
                            ticks: {
                                display: false
                            },
                            gridLines: {
                                borderDash: [2, 2]
                            }
                        }
                    ],
                    xAxes: [
                        {
                            display: false,
                            gridLines: {
                                display: false
                            },
                            ticks: {
                                display: false
                            }
                        }
                    ]
                },
                onHover: (event, chartElement) => {
                    event.target.style.cursor = chartElement[0] ? 'pointer' : 'default';
                },
                tooltips: {
                    intersect: false,
                    mode: 'index',
                    callbacks: {
                        label: (tooltipItem) => {
                            return tooltipItem.yLabel;
                        }
                    }
                },
                responsive: true,
                maintainAspectRatio: false
            }}
        />
    );
};

export default CVELineChart;
